import type {
    FilteredItemsPageUrlQueryParams,
    ItemFiltersRequestData,
    ItemsCategoryPropertyFiltersFormValues,
    ItemsListRouteParams,
} from '@frontend/modules/public/ads/AdsModuleTypes'
import {
    type MouseEvent,
    type SubmitEvent,
    useEffect,
    useRef,
    useState,
} from 'react'
import {useParams} from 'react-router-dom'
import {useEventCallback} from 'swayok-react-mdb-ui-kit/helpers/useEventCallback'
import {useUrlQueryParams} from 'swayok-react-mdb-ui-kit/helpers/useUrlQueryParams'
import type {ReactSetState} from 'swayok-react-mdb-ui-kit/types'
import {convertItemFiltersFromUrlQueryToFiltersForRequest} from '../../../../helpers/convertItemFiltersFromUrlQueryToFiltersForRequest'
import {extractIdFromUrlSlug} from '../../../../helpers/extractIdFromUrlSlug'
import {makeObjectHash} from '../../../../helpers/makeObjectHash'
import {modifyItemsSearchUrlQueryParams} from '../../../../helpers/modifyItemsSearchUrlQueryParams'

export interface UseAdCategoryFiltersFormHookReturn {
    filters: ItemsCategoryPropertyFiltersFormValues
    setFilters: ReactSetState<ItemsCategoryPropertyFiltersFormValues>
    isSubmitting: boolean
    onSubmit: (event?: MouseEvent<HTMLElement> | SubmitEvent<HTMLElement>) => void
    resetFromUrl: () => void
    urlQueryData: FilteredItemsPageUrlQueryParams
    categoryId: number | null
}

// Значения фильтров объявлений, синхронизированные с URL query, и их применение.
export function useAdCategoryFiltersForm(
    onSubmitted?: () => void
): UseAdCategoryFiltersFormHookReturn {

    const {
        setUrlQueryParams,
        urlQueryData,
    } = useUrlQueryParams<FilteredItemsPageUrlQueryParams>()

    // Фильтры.
    const [
        filters,
        setFilters,
    ] = useState<ItemsCategoryPropertyFiltersFormValues>({})

    // Предотвращение спама.
    const [
        isSubmitting,
        setIsSubmitting,
    ] = useState<boolean>(false)

    // Хэш предыдущих параметров URL query.
    const prevUrlQueryHashRef = useRef<string | null>(null)

    // Данные о текущем URL.
    const routeParams = useParams<ItemsListRouteParams>()
    const categoryId: number | null = extractIdFromUrlSlug(routeParams.slugWithId)

    // Обновление состояний при изменении соответствующих параметров в URL query.
    useEffect(() => {
        const newHash: string = makeObjectHash(urlQueryData)
        if (newHash === prevUrlQueryHashRef.current) {
            // Нет изменений в URL query параметрах.
            return
        }
        convertItemFiltersFromUrlQueryToFiltersForRequest(urlQueryData)
            .then(requestData => {
                setFilters(filters => ({
                    ...filters,
                    ...extractFormValuesFromRequestData(requestData),
                }))
            })
            .catch(e => {
                // Этого не должно происходить, но лучше обработать ошибку.
                console.error(e)
            })
        prevUrlQueryHashRef.current = newHash
    }, [urlQueryData.minPrice, urlQueryData.maxPrice, urlQueryData.cm, urlQueryData.f])

    // Отменить изменения, которые еще не применены.
    const resetFromUrl = useEventCallback(() => {
        convertItemFiltersFromUrlQueryToFiltersForRequest(urlQueryData)
            .then(requestData => {
                setFilters(extractFormValuesFromRequestData(requestData))
            })
            .catch(e => {
                // Этого не должно происходит, но лучше обработать ошибку.
                console.error(e)
            })
    })

    // Применение фильтров.
    const onSubmit = useEventCallback((
        event?: MouseEvent<HTMLElement> | SubmitEvent<HTMLElement>
    ) => {
        event?.preventDefault()
        onSubmitted?.()
        if (isSubmitting) {
            return
        }
        // Блокировка повторных запросов на небольшое время, чтобы предотвратить спам.
        setIsSubmitting(true)
        setTimeout(() => setIsSubmitting(false), 1500)

        modifyItemsSearchUrlQueryParams(
            urlQueryData,
            {},
            {
                ...filters,
                // Выбранные оба способа связи дают тот же результат, что и фильтр без значения.
                communicationMethod: filters.communicationMethod?.length === 1
                    ? filters.communicationMethod
                    : undefined,
            }
        )
            .then(newQueryParams => {
                prevUrlQueryHashRef.current = makeObjectHash(newQueryParams)
                setUrlQueryParams(new URLSearchParams(newQueryParams))
            })
            .catch(e => {
                console.error(e)
            })
    })

    return {
        filters,
        setFilters,
        isSubmitting,
        onSubmit,
        resetFromUrl,
        urlQueryData,
        categoryId,
    }
}

// Преобразует данные запроса в значения формы фильтров.
function extractFormValuesFromRequestData(
    request: ItemFiltersRequestData
): ItemsCategoryPropertyFiltersFormValues {
    return {
        minPrice: request.minPrice,
        maxPrice: request.maxPrice,
        communicationMethod: request.communicationMethod ? [request.communicationMethod] : [],
        props: request.props,
    }
}
